import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Toast } from 'antd-mobile';
import { useIframeHeightSync } from '@/hooks/useIframeHeightSync';
import {
  getSupervisorAnalysisDetail,
  getSupervisorAnalysisByFileId,
  chooseScenario,
  ChooseScenarioParams
} from '@/lib/api/ai-supervisor';
import ManyDotLoading from '@/components/Loaders/ManyDotLoading';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';

// 组件导入
import ScenarioSelector from './components/ScenarioSelector';
import AnalysisResult from './components/AnalysisResult';

const AiSupervisorAnalysis: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // 使用iframe高度自适应hook
  useIframeHeightSync({
    minHeight: 400, // 设置一个合理的最小高度
    enabled: true,
    debounceDelay: 800, // 增加防抖延迟，避免动画期间频繁触发
  });

  const analysisId = searchParams.get('analysisId');
  const fileId = searchParams.get('fileId');
  const agentId = searchParams.get('agentId');
  const corpId = searchParams.get('corpId');
  const userId = searchParams.get('userId');
  const instanceId = searchParams.get('instanceId');
  const unionId = searchParams.get('unionId');
  const embedded = searchParams.get('embedded');

  console.log('🔍 [AiSupervisorAnalysis] 组件初始化 - URL参数:', {
    analysisId,
    fileId,
    agentId,
    corpId,
    userId,
    instanceId,
    unionId,
    allSearchParams: Object.fromEntries(searchParams.entries())
  });

  const [isLoading, setIsLoading] = useState(false);
  const [apiData, setApiData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [showScenarioSelector, setShowScenarioSelector] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);

  // 监控状态变化
  useEffect(() => {
    console.log('🔄 [State Monitor] 状态变化:', {
      isLoading,
      hasApiData: !!apiData,
      error,
      showScenarioSelector,
      isPolling,
      selectedScenarioId
    });
  }, [isLoading, apiData, error, showScenarioSelector, isPolling, selectedScenarioId]);

  // 判断是否是钉钉模式
  const isDingTalkMode = !!fileId && !!agentId && !!corpId && !!userId && !!instanceId;
  console.log('🔍 [AiSupervisorAnalysis] 模式判断:', {
    isDingTalkMode,
    hasFileId: !!fileId,
    hasAgentId: !!agentId,
    hasCorpId: !!corpId,
    hasUserId: !!userId,
    hasInstanceId: !!instanceId
  });

  // 获取督导分析数据（普通模式）
  const fetchSupervisorDetail = async () => {
    console.log('📡 [fetchSupervisorDetail] 开始获取督导分析详情', { analysisId });

    if (!analysisId) {
      console.error('❌ [fetchSupervisorDetail] 缺少 analysisId');
      setError('未找到分析ID');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    console.log('⏳ [fetchSupervisorDetail] 设置 loading 状态为 true');

    try {
      console.log('📤 [fetchSupervisorDetail] 调用 API:', { analysisId });
      const response = await getSupervisorAnalysisDetail(analysisId);
      console.log('📥 [fetchSupervisorDetail] API 响应:', response);

      if (response.success && response.data) {
        console.log('✅ [fetchSupervisorDetail] 获取数据成功:', response.data);
        setApiData(response.data);
        setIsLoading(false);
      } else {
        console.error('❌ [fetchSupervisorDetail] 响应失败:', response);
        throw new Error('获取督导分析详情失败');
      }
    } catch (error) {
      console.error('❌ [fetchSupervisorDetail] 捕获异常:', error);
      setError('获取分析详情失败');
      Toast.show({ content: '获取分析数据失败', icon: 'fail' });
      setIsLoading(false);
    }
  };

  // 钉钉模式：通过 fileId 查询分析结果
  const fetchAnalysisByFileId = async (scenarioId?: string) => {
    console.log('📡 [fetchAnalysisByFileId] 开始查询分析结果', { fileId, scenarioId });

    if (!fileId) {
      console.error('❌ [fetchAnalysisByFileId] 缺少 fileId');
      return null;
    }

    try {
      console.log('📤 [fetchAnalysisByFileId] 调用 API:', { fileId, scenarioId });
      const response = await getSupervisorAnalysisByFileId(fileId, scenarioId);
      console.log('📥 [fetchAnalysisByFileId] API 响应:', response);

      if (response.success && response.data) {
        console.log('✅ [fetchAnalysisByFileId] 获取到分析结果:', response.data);
        // 有分析结果，展示结果
        setApiData(response.data);
        setShowScenarioSelector(false);
        setIsPolling(false);
        console.log('🔄 [fetchAnalysisByFileId] 状态更新: apiData 已设置, showScenarioSelector=false, isPolling=false');
        return response.data;
      } else {
        console.warn('⚠️ [fetchAnalysisByFileId] 没有分析结果');
        // 没有分析结果，显示场景选择
        return null;
      }
    } catch (error) {
      console.error('❌ [fetchAnalysisByFileId] 查询分析结果失败:', error);
      return null;
    }
  };

  // 钉钉模式：选择场景并开始分析
  const handleScenarioSelect = async (scenarioId: string) => {
    console.log('🎯 [handleScenarioSelect] ========== 开始选择场景 ==========');
    console.log('🎯 [handleScenarioSelect] 接收参数:', { scenarioId });
    console.log('🎯 [handleScenarioSelect] 当前状态:', {
      fileId,
      agentId,
      corpId,
      userId,
      instanceId,
      unionId
    });

    if (!fileId || !agentId || !corpId || !userId || !instanceId) {
      console.error('❌ [handleScenarioSelect] 缺少必要参数:', {
        fileId: !!fileId,
        agentId: !!agentId,
        corpId: !!corpId,
        userId: !!userId,
        instanceId: !!instanceId
      });
      Toast.show({ content: '缺少必要参数', icon: 'fail' });
      return;
    }

    console.log('🔄 [handleScenarioSelect] 设置状态: selectedScenarioId =', scenarioId);
    setSelectedScenarioId(scenarioId);
    console.log('⏳ [handleScenarioSelect] 设置状态: isLoading = true');
    setIsLoading(true);

    try {
      // 调用选择场景接口
      const params: ChooseScenarioParams = {
        agentId,
        corpId,
        userId,
        fileId,
        instanceId,
        scenario_id: scenarioId,
        union_id: unionId || undefined
      };

      console.log('📤 [handleScenarioSelect] 调用 chooseScenario API, 参数:', params);
      const response = await chooseScenario(params);
      console.log('📥 [handleScenarioSelect] API 响应:', response);

      if (response.success) {
        console.log('✅ [handleScenarioSelect] 场景选择成功');
        Toast.show({ content: '已开始分析，正在生成结果...', icon: 'success' });
        console.log('🔄 [handleScenarioSelect] 设置状态: showScenarioSelector = false');
        setShowScenarioSelector(false);

        console.log('🔁 [handleScenarioSelect] 开始轮询，调用 startPolling');
        // 开始轮询查询结果
        startPolling(scenarioId);
      } else {
        console.error('❌ [handleScenarioSelect] 场景选择失败，响应不成功:', response);
        throw new Error('选择场景失败');
      }
    } catch (error) {
      console.error('❌ [handleScenarioSelect] 捕获异常:', error);
      Toast.show({ content: '选择场景失败', icon: 'fail' });
      console.log('🔄 [handleScenarioSelect] 设置状态: isLoading = false');
      setIsLoading(false);
    }
    console.log('🎯 [handleScenarioSelect] ========== 选择场景流程结束 ==========');
  };

  // 开始轮询查询分析结果
  const startPolling = (scenarioId: string) => {
    console.log('🔁 [startPolling] ========== 开始轮询 ==========');
    console.log('🔁 [startPolling] 轮询参数:', { scenarioId, fileId });
    console.log('🔄 [startPolling] 设置状态: isPolling = true');
    setIsPolling(true);

    let pollCount = 0;
    const pollInterval = setInterval(async () => {
      pollCount++;
      console.log(`🔁 [startPolling] 第 ${pollCount} 次轮询`);

      if (!fileId) {
        console.error('❌ [startPolling] 缺少 fileId，停止轮询');
        clearInterval(pollInterval);
        return;
      }

      try {
        console.log('📤 [startPolling] 调用 getSupervisorAnalysisByFileId:', { fileId, scenarioId });
        const response = await getSupervisorAnalysisByFileId(fileId, scenarioId);
        console.log(`📥 [startPolling] 第 ${pollCount} 次轮询响应:`, response);

        if (response.success && response.data) {
          console.log('✅ [startPolling] 获取到分析结果，停止轮询');
          console.log('📊 [startPolling] 分析结果数据:', response.data);
          // 获取到分析结果
          setApiData(response.data);
          setIsLoading(false);
          setIsPolling(false);
          console.log('🔄 [startPolling] 状态更新: isLoading=false, isPolling=false');
          clearInterval(pollInterval);
        } else {
          console.log(`⏳ [startPolling] 第 ${pollCount} 次轮询暂无结果，继续等待...`);
        }
      } catch (error) {
        console.error(`❌ [startPolling] 第 ${pollCount} 次轮询查询失败:`, error);
      }
    }, 3000); // 每3秒轮询一次

    console.log('⏰ [startPolling] 设置轮询超时定时器（5分钟）');
    // 设置轮询超时（5分钟）
    setTimeout(() => {
      console.log('⏰ [startPolling] 轮询超时检查');
      clearInterval(pollInterval);
      if (isPolling) {
        console.error('❌ [startPolling] 轮询超时，停止轮询');
        setIsLoading(false);
        setIsPolling(false);
        Toast.show({ content: '分析超时，请稍后重试', icon: 'fail' });
      }
    }, 5 * 60 * 1000);
  };

  // 初始化 - 首次进入页面
  useEffect(() => {
    console.log('🔄 [useEffect-Init] ========== 组件首次初始化 ==========');
    console.log('🔄 [useEffect-Init] 依赖项变化:', { analysisId, fileId, isDingTalkMode });

    if (isDingTalkMode) {
      console.log('📱 [useEffect-Init] 进入钉钉模式初始化');

      // 检查URL中是否有 scenarioId 参数
      const urlScenarioId = searchParams.get('scenarioId');
      console.log('🔍 [useEffect-Init] 检查 URL 中的 scenarioId:', urlScenarioId);

      // 先尝试查询最新的分析结果（不管有没有 scenarioId）
      console.log('⏳ [useEffect-Init] 设置 isLoading = true');
      setIsLoading(true);

      fetchAnalysisByFileId(urlScenarioId || undefined).then((result) => {
        console.log('📥 [useEffect-Init] fetchAnalysisByFileId 结果:', result);
        if (result === null) {
          // 没有分析结果，显示场景选择
          console.log('⚠️ [useEffect-Init] 没有分析结果，显示场景选择');
          setShowScenarioSelector(true);
          setIsLoading(false);
        } else {
          // 有分析结果，fetchAnalysisByFileId 内部已经设置了 apiData 和 isLoading
          console.log('✅ [useEffect-Init] 找到分析结果，已设置 apiData');
          setIsLoading(false);
        }
      });
    } else {
      console.log('💼 [useEffect-Init] 进入普通模式初始化');
      // 普通模式
      fetchSupervisorDetail();
    }
    console.log('🔄 [useEffect-Init] ========== 首次初始化结束 ==========');
  }, [analysisId, fileId]);

  // 返回列表
  const backToList = () => {
    console.log('🔙 [backToList] 返回列表页面');
    navigate('/ai-supervisor/list');
  };

  // 错误处理渲染
  if (error) {
    console.log('🎨 [Render] 渲染错误页面:', error);
    return (
      <PCMaxWidthContainer bgGradient='bg-gradient-to-b from-[#f8f9ff] to-[#ffffff]'>
      <div className='min-h-screen p-5 flex items-center justify-center'>
        <div className='text-center'>
          <div className='text-red-500 text-lg mb-4'>加载失败</div>
          <div className='text-gray-600 text-sm'>{error}</div>
          <button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              if (isDingTalkMode && selectedScenarioId) {
                fetchAnalysisByFileId(selectedScenarioId);
              } else {
                fetchSupervisorDetail();
              }
            }}
            className='mt-4 px-4 py-2 bg-gradient-to-r from-[#a782ff] to-[#828eff] text-white rounded-full hover:shadow-lg'
          >
            重试
          </button>
        </div>
      </div>
      </PCMaxWidthContainer>
    );
  }

  // 加载中
  if (isLoading || isPolling) {
    console.log('🎨 [Render] 渲染加载页面:', { isLoading, isPolling });
    return (
      <PCMaxWidthContainer bgGradient='bg-gradient-to-b from-[#f8f9ff] to-[#ffffff]'>
      <div className='min-h-screen p-5 flex items-center justify-center'>
        <div className='flex items-center flex-col'>
          <ManyDotLoading />
          <div className='text-[#303133] text-lg font-medium mt-6'>
            正在为您生成分析报告...
          </div>
          <div className='text-[#909399] text-sm mt-2'>
            {isPolling ? '正在轮询分析结果...' : '正在进行话术推荐...'}
          </div>
        </div>
      </div>
      </PCMaxWidthContainer>
    );
  }

  // 显示场景选择
  if (showScenarioSelector && isDingTalkMode) {
    console.log('🎨 [Render] 渲染场景选择页面');
    return <ScenarioSelector onScenarioSelect={handleScenarioSelect} />;
  }

  // 显示分析结果
  if (apiData) {
    console.log('🎨 [Render] 渲染分析结果页面:', { apiDataKeys: Object.keys(apiData) });
    return <AnalysisResult apiData={apiData} onBack={isDingTalkMode ? undefined : backToList} embedded={!!embedded} agentId={agentId} />;
  }

  // 默认返回空状态
  console.log('🎨 [Render] 渲染空状态页面');
  return (
    <PCMaxWidthContainer bgGradient='bg-gradient-to-b from-[#f8f9ff] to-[#ffffff]'>
    <div className='min-h-screen p-5 flex items-center justify-center'>
      <div className='text-center'>
        <div className='text-gray-600 text-sm'>暂无数据</div>
      </div>
    </div>
    </PCMaxWidthContainer>
  );
};

export default AiSupervisorAnalysis;
